import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import Config from "../Config.json";
import { useNavigate, useParams } from "react-router-dom";
import { getArticles, getArticleDetails, updateArticleVote } from "../Fetching";
import { useTranslation } from "react-i18next";

const BlogEn = () => {
  const TITLE = Config.SITE_TITLE + " | Blog";
  const DESC = "Create a React App from an HTML Website.";
  const CANONICAL = Config.SITE_DOMAIN + "/";

  const { lng } = useParams();
  const { i18n, t } = useTranslation();
  const language = lng || i18n.language || "en";

  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");

  const parseTimeToMs = (t) => {
    if (!t) return 0;
    const iso = t.replace(" ", "T");
    const d = new Date(iso);
    return isNaN(d.getTime()) ? 0 : d.getTime();
  };

  const calculateElapsedTime = (timeString) => {
    const publicationDate = new Date(timeString);
    const now = new Date();
    const diffInMs = now - publicationDate;

    const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    return `${diffInDays} days ago`;
  };

  // 📌 Cikkek lekérése és rendezése legújabb elöl
  useEffect(() => {
    const fetchArticles = async () => {
      try {
        const data = await getArticleDetails(language);
        const uniqueArticles = data.reduce((acc, article) => {
          if (!acc.some((item) => item.ArtId === article.ArtId)) acc.push(article);
          return acc;
        }, []);

        // 🔹 Legfrissebb cikkek elöl
        const sorted = (uniqueArticles || [])
          .slice()
          .sort((a, b) => parseTimeToMs(b.time) - parseTimeToMs(a.time));

        setArticles(sorted);
      } catch (error) {
        console.error("Hiba a cikkek betöltésekor:", error);
      }
    };

    fetchArticles();
  }, [language]);

  // 📌 Szűrt és rendezett lista
  const filteredArticles = (
    selectedCategory === "All"
      ? articles
      : articles.filter((article) => {
          const categoryMap = {
            All: 0,
            Trend: 1,
            "Machine Learning": 2,
            "AI Tools": 3,
            "AI Games": 4,
          };
          return article.category === categoryMap[selectedCategory];
        })
  )
    .slice()
    .sort((a, b) => parseTimeToMs(b.time) - parseTimeToMs(a.time)); // újabbak előre

  // 📌 Vissza a tetejére gomb
  useEffect(() => {
    const mybutton = document.getElementById("TopBtn");
    if (!mybutton) return;

    const scrollFunction = () => {
      mybutton.style.display = window.scrollY > 100 ? "block" : "none";
    };

    const topFunction = () => {
      window.scrollTo({ top: 0, behavior: "smooth" });
    };

    window.addEventListener("scroll", scrollFunction);
    mybutton.addEventListener("click", topFunction);

    return () => {
      window.removeEventListener("scroll", scrollFunction);
      mybutton.removeEventListener("click", topFunction);
    };
  }, []);

  // 📌 Szavazás
  const handleVote = async (artId, action) => {
    try {
      await updateArticleVote(artId, action, language);
      setArticles((prevArticles) =>
        prevArticles.map((article) => {
          if (article.ArtId === artId) {
            return {
              ...article,
              plus: action === "like" ? article.plus + 1 : article.plus,
              minus: action === "dislike" ? article.minus + 1 : article.minus,
            };
          }
          return article;
        })
      );
    } catch (error) {
      console.error("Error while voting:", error);
    }
  };

  // 📌 Navigálás az új URL-sémával (artID az útvonalban)
  const handleReadMore = (artId) => {
    navigate(`/article/${artId}?lang=${language}`);
  };

  const categoryTranslations = {
    All: t("FilterButton1Title"),
    Trend: t("FilterButton3Title"),
    "Machine Learning": "Machine Learning",
    "AI Tools": "AI Tools",
    "AI Games": "AI Games",
  };

  return (
    <main className="main-content">
      <Helmet>
        <title>{TITLE}</title>
        <meta name="description" content={DESC} />
        <link rel="canonical" href={CANONICAL} />
      </Helmet>

      <button id="TopBtn" title="Go to top">
        &#10224;
      </button>

      <div className="container">
        <div className="row mb-4">
          <div className="col-12 d-flex flex-wrap justify-content-center">
            {["All", "Trend", "Machine Learning", "AI Tools", "AI Games"].map(
              (cat) => (
                <button
                  key={cat}
                  className={`btn btn-AIinsideLife-primary m-2 ${
                    selectedCategory === cat ? "active" : ""
                  }`}
                  onClick={() => setSelectedCategory(cat)}
                >
                  {categoryTranslations[cat] || cat}
                </button>
              )
            )}
          </div>
        </div>

        <div className="row">
          <h1 className="text-center">
            {categoryTranslations[selectedCategory] || selectedCategory}
          </h1>
        </div>

        {filteredArticles.length === 0 ? (
          <p className="text-center">
            Nincs elérhető cikk ebben a kategóriában.
          </p>
        ) : (
          <div className="row g-3">
            {filteredArticles.map((article) => (
              <div className="col-sm-12 col-md-6 col-lg-3" key={article.ArtId}>
                <div className="card h-100">
                  <img
                    src={
                      article.img
                        ? `/articleImages/${article.img.split("/").pop()}`
                        : "https://via.placeholder.com/300"
                    }
                    className="card-img-top"
                    alt={article.alt || "Article Image"}
                    onError={(e) => {
                      if (!e.target.dataset.errorHandled) {
                        e.target.dataset.errorHandled = "true";
                        e.target.src = "https://via.placeholder.com/300";
                      }
                    }}
                  />
                  <div className="card-body">
                    <h5 className="card-title">{article.Title}</h5>
                    <p className="card-text text-truncate">{article.Teaser}</p>
                    <p className="text-muted">
                      Published: {calculateElapsedTime(article.time)}
                    </p>
                    <div className="d-flex justify-content-between">
                      <button
                        className="btn btn-outline-success btn-sm"
                        onClick={() => handleVote(article.ArtId, "like")}
                      >
                        👍 {article.plus}
                      </button>
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleVote(article.ArtId, "dislike")}
                      >
                        👎 {article.minus}
                      </button>
                    </div>
                    <button
                      onClick={() => handleReadMore(article.ArtId)}
                      className="btn btn-AIinsideLife-primary w-100 mt-2"
                    >
                      Read More...
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
};

export default BlogEn;
